import { useState, useCallback, useEffect } from 'react';
import { getUserBusinesses, deleteBusiness } from '../data/repositories/businessRepository.js';

export const useMyBusinesses = () => {
    const [businesses, setBusinesses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(() => {
        // Initialize from localStorage if available
        const savedPage = localStorage.getItem('myBusinessesCurrentPage');
        return savedPage ? parseInt(savedPage, 10) : 1;
    });
    const [totalCount, setTotalCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');

    const itemsPerPage = 10;

    const fetchBusinesses = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            setError(null);
            const data = await getUserBusinesses(page, itemsPerPage);

            // We use the normalized response from the repository
            const businessesArray = data.data || [];
            const total = data.meta?.totalRecords || businessesArray.length;

            setBusinesses(businessesArray);
            setCurrentPage(page);
            // Save current page to localStorage
            localStorage.setItem('myBusinessesCurrentPage', page.toString());
            setTotalCount(total);

            return { businesses: businessesArray, total };
        } catch (err) {
            console.error('Fetch error:', err);
            setError(err.message || 'Failed to fetch businesses');
            setBusinesses([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleDelete = useCallback(async (businessId) => {
        if (!window.confirm('Are you sure you want to delete this business?')) return;
        try {
            await deleteBusiness(businessId);
            setError(null);
            // Fetch current page again to update list
            await fetchBusinesses(currentPage);
        } catch (err) {
            console.error('Delete error:', err);
            setError(err.message || 'Failed to delete business');
        }
    }, [currentPage, fetchBusinesses]);

    const handleSearch = useCallback((query) => {
        setSearchQuery(query);
    }, []);

    // Filter businesses locally based on search query
    const filteredBusinesses = businesses.filter((business) => {
        if (!searchQuery.trim()) return true;
        
        const query = searchQuery.toLowerCase();
        const name = business.name?.toLowerCase() || '';
        const categoryName = business.category?.name?.toLowerCase() || '';
        const entity = business.entity?.toLowerCase() || '';
        const address = business.address?.toLowerCase() || business.mapAddress?.generalAddress?.toLowerCase() || '';
        const contact = business.contact?.toLowerCase() || '';

        return (
            name.includes(query) ||
            categoryName.includes(query) ||
            entity.includes(query) ||
            address.includes(query) ||
            contact.includes(query)
        );
    });

    return {
        businesses: filteredBusinesses,
        originalBusinesses: businesses,
        loading,
        error,
        currentPage,
        totalCount,
        totalPages: Math.ceil(totalCount / itemsPerPage),
        searchQuery,
        setSearchQuery: handleSearch,
        fetchBusinesses,
        handleDelete,
    };
};
